/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import React, { useEffect, useState } from 'react';

import Input from '@/lib/Input';
import Button from '@/lib/Button';
import Icon from '@/lib/Icon/Icon';
import CheckBox from '@/lib/CheckBox';
import { useGlobalStore } from '@/store';
import {
  CONTACT_REGEX,
  CUSTOMER_ACCESS_TOKEN_KEY,
  INDIAN_CONTACT_COUNTRY_CODE,
  USER_TYPE,
} from '@/constants';
import OtpModal from '@/views/Landing/OtpModal';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import { fetchCustomerDetails } from '@/api/customer';
import CollectPanModal from '@/shared/Components/CollectPanModal';
import EmiPlansLoader from '@/shared/Components/EmiPlansLoader';
import { IUser } from '@/@types/globals';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';
import TruncateComponent from '@/shared/Components/TruncateComponent';
import {
  trackConsentBoxClicked,
  trackEditContactClicked,
  trackInputClicked,
  trackLandingScreenRender,
  trackLandingCTAClick,
} from '@/analytics/views/landing';
import { formatContactNumber } from '@/utils/helpers';
import { updatePaymentDataInAnalytics } from '@/analytics/utils';

const UserLogin: React.FC = () => {
  const user = useGlobalStore((store) => store.user);
  const [contactError, setContactError] = useState('');
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [isCTALoading, setIsCTALoading] = useState(false);
  const setUser = useGlobalStore((store) => store.setUser);
  const [contact, setContact] = useState(user.contact ?? '');
  const setView = useGlobalStore((store) => store.setActiveView);
  const { userAccessToken, paymentDetails } = useGlobalStore();
  const [showCollectPanModal, setShowCollectPanModal] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [isTermsAndConditionChecked, setIsTermsAndConditionChecked] = useState(false);
  const [fetchEmiPLans, setFetchEmiPlans] = useState(false);
  const [showContactInput, setShowContactInput] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [errorMessage, setErrorMsg] = useState('');

  const getUserLoggedInStatus = (token: string | null, userContact = ''): string => {
    return Boolean(token && userContact) ? USER_TYPE.LOGGED_IN : USER_TYPE.NON_LOGGED_IN;
  };

  useEffect(() => {
    if (user.contact) {
      setContact(user.contact);
    }
  }, [user]);

  useEffect(() => {
    if (userAccessToken && user.contact) {
      setIsUserLoggedIn(true);
    }
    // Track render event
    updatePaymentDataInAnalytics();
    trackLandingScreenRender({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      contact: user.contact,
    });
  }, [userAccessToken, user.contact]);

  const handleRedirectionToNextScreen = (currentUser: IUser): void => {
    // If PAN is not present, show CollectPanModal
    if (!currentUser.pan) {
      return setShowCollectPanModal(true);
    }

    if (!currentUser.pin_code) {
      return setView('DETAILS');
    }

    setFetchEmiPlans(true);
  };

  const getCustomerDetails = async (): Promise<void> => {
    setIsCTALoading(true);
    const customerDetails = await fetchCustomerDetails({
      [CUSTOMER_ACCESS_TOKEN_KEY]: userAccessToken ?? '',
    });

    if (customerDetails.success && customerDetails.data) {
      setIsCTALoading(false);

      setUser(customerDetails.data);
      // Get the necessary details that are not available from user
      handleRedirectionToNextScreen(customerDetails.data);
    } else if (customerDetails.error) {
      setCustomerError(true);
      setErrorMsg(customerDetails.error);
    }
  };

  const handleSentOTP = async (): Promise<void> => {
    setContactError('');
    if (!isTermsAndConditionChecked) return;

    if (contact && CONTACT_REGEX.test(contact)) {
      if (user.contact !== contact) {
        setUser({
          contact: !contact.includes(INDIAN_CONTACT_COUNTRY_CODE)
            ? `${INDIAN_CONTACT_COUNTRY_CODE}${contact}`
            : contact,
        });
      }

      setIsCTALoading(true);

      if (!isUserLoggedIn) {
        setShowOTPModal(true);
        // Track send otp cta clicked
        trackLandingCTAClick(
          {
            contact: formatContactNumber(contact),
          },
          'otp',
        );
      } else {
        // Track send otp cta clicked
        trackLandingCTAClick(
          {
            contact: formatContactNumber(contact),
            user_type: USER_TYPE.LOGGED_IN,
          },
          'login',
        );
        await getCustomerDetails();
      }
    } else {
      setContactError('Invalid contact number');
    }
  };

  const handleContactEdit = (): void => {
    setShowContactInput(true);
    // track edit contact clicked
    trackEditContactClicked({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      contact: user.contact,
    });
  };

  const handleInputFocus = (): void => {
    if (!contact) {
      // track input focus where contact was not already present
      trackInputClicked();
    }
  };

  const handleTermsClick = (): void => {
    setIsTermsAndConditionChecked((current) => !current);
    // Track the positive selection of checkbox for tnc
    if (!isTermsAndConditionChecked) {
      trackConsentBoxClicked({
        contact: formatContactNumber(contact),
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
      });
    }
  };

  return (
    <div className="px-5 py-2 shadow-top shrink-0 align-bottom d:flex d:justify-center w-full">
      <div className="w-full d:w-1/2">
        {(!user.contact && !paymentDetails.loading) || showContactInput ? (
          <>
            <span className="text-sm text-on-grey-subtle">Get started by entering your number</span>
            <Input
              data-testid="contact-input"
              error={contactError}
              type="number"
              value={
                contact.includes(INDIAN_CONTACT_COUNTRY_CODE)
                  ? contact.split(INDIAN_CONTACT_COUNTRY_CODE)[1]
                  : contact
              }
              onChange={(e): void => {
                setContact(e.target.value);
                setIsUserLoggedIn(false);
              }}
              onFocus={handleInputFocus}
            />
          </>
        ) : (
          <span className="text-sm text-on-grey-subtle mt-2 mb-3">
            Continue with <strong>{contact}</strong>
            <button
              data-testid="edit-contact"
              className="relative top-0.5 ml-1"
              onClick={handleContactEdit}
            >
              <Icon name="edit" className="mx-auto" />
            </button>
          </span>
        )}

        <CheckBox checked={isTermsAndConditionChecked} onClick={handleTermsClick}>
          <TruncateComponent
            htmlString={`I agree to Razorpay's <a class="underline" href="https://razorpay.com/terms/capital/" target="_blank" rel="noreferrer"> T&C</a> &  <a class="underline" href="https://razorpay.com/privacy/capital/" target="_blank" rel="noreferrer">Privacy Policy</a>. I grant my consent to lenders providing services through Razorpay&apos;s platform, to
          fetch/utilize my KYC records from CKYCR and UIDAI, as well as Credit Information from
          bureaus for lending purposes.`}
            maxLength={250}
          />
        </CheckBox>

        <div className="border-t border-outline-grey-muted mt-3">
          <Button disabled={!isTermsAndConditionChecked} onClick={handleSentOTP}>
            {isUserLoggedIn ? 'Get Started' : 'Send OTP'}
          </Button>
          <SecuredByRazorpay />
        </div>
      </div>

      {showOTPModal ? (
        <OtpModal
          contact={contact}
          isOpen={showOTPModal}
          onSuccess={(userRes: { data: IUser }): void => {
            setShowOTPModal(false);
            handleRedirectionToNextScreen(userRes.data);
            setIsCTALoading(false);
          }}
          onDismiss={(): void => {
            setShowOTPModal(false);
          }}
        />
      ) : null}

      {showCollectPanModal ? (
        <CollectPanModal
          onSuccess={(updatedUser): void => {
            setShowCollectPanModal(false);
            handleRedirectionToNextScreen(updatedUser);
          }}
          isOpen={showCollectPanModal}
          onDismiss={(): void => {
            setShowCollectPanModal(false);
          }}
        />
      ) : null}

      {fetchEmiPLans ? (
        <EmiPlansLoader
          isLoading={fetchEmiPLans}
          onDismiss={(): void => {
            setFetchEmiPlans(false);
          }}
        />
      ) : null}

      {customerError ? (
        <PaymentCancelWrapper
          isOpen={customerError}
          onDismiss={(): void => {
            setCustomerError(false);
            setShowCollectPanModal(false);
          }}
          title="Error fetching customer"
          description={errorMessage}
          ctaText="Try Again"
          hideCta={true}
        />
      ) : null}
    </div>
  );
};

export default UserLogin;
